import { FC, useState } from 'react';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';

// Scripts
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentAccolades } from 'graphqlTypes';

interface AccoladesProps extends FlexProps, ContentfulComponentAccolades {
  backgroundIsDark: boolean;
}

const Accolades: FC<AccoladesProps> = ({ heading, company, ...props }) => {
  const [expand, setExpend] = useState(false);
  const length = company?.length || 0;

  return (
    <Flex
      position="relative"
      borderRadius="24px"
      backgroundColor="navy-900"
      paddingAll={48}
      overflowX="hidden"
      overflowY="hidden"
      lg={{ paddingAll: 64 }}
      {...props}
    >
      <Flex
        position="relative"
        width="100%"
        height="100%"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        gap="40px"
        zIndex="1"
      >
        {heading && (
          <Heading as="h3" textStyle="h3" textColor="common-white" md={{ textStyle: 'h3' }}>
            {heading}
          </Heading>
        )}
        {company && (
          <Flex
            maxWidth="calc(32px * 4 + 200px * 4)"
            flexWrap="wrap"
            width="100%"
            gap="32px"
            alignItems="flex-start"
            justifyContent="space-around"
            lg={{ justifyContent: 'space-between' }}
          >
            {company.slice(0, expand ? length : 4).map(item => (
              <Flex
                key={item?.company}
                flexDirection="column"
                gap="12px"
                width="100%"
                height="100%"
                maxHeight="140px"
                alignItems="center"
                justifyContent="space-between"
                className="max-w-[100px] sm:max-w-[140px]"
              >
                {item?.logo && (
                  <Container width="100%" maxWidth="200px">
                    <Image
                      image={item.logo as ImageData}
                      alt=""
                      width="100%"
                      height="100%"
                      maxHeight="100px"
                      objectFit="contain"
                      objectPosition="center"
                    />
                  </Container>
                )}
                {item?.altText && (
                  <Flex fontSize="textSm" textColor="common-white">
                    {richTextParser(item.altText)}
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        )}
        {length > 4 && (
          <Flex
            gap="4px"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={() => setExpend(!expand)}
          >
            <ComponentButton
              isButton
              label={expand ? 'Show less' : 'See all awards'}
              buttonColor="Gray 900 Link"
              css={`
               color: ${color.common.white} !important;
               &.MuiButton-root {
                 pointer-events: none;
               },
             `}
            />
            <Flex transition="transform 100ms ease-in-out" transform={expand ? 'rotate(180deg)' : 'inherit'}>
              <Icon id="chevron-down" fill={color.common.white} size={20} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default Accolades;
